import React, { useEffect, useRef } from 'react';
import './HoliColorEffect.css';

const HoliColorEffect = ({ containerRef }) => {
    const canvasRef = useRef(null);
    const particles = useRef([]);
    const animationFrameId = useRef(null);
    const isInitialBurstDone = useRef(false);

    // Configuration
    const GRAVITY = 0.05; // Reduced for slower, more graceful fall
    const FRICTION = 0.985; // Slightly more air resistance
    // Darker, rich red holi colors
    const COLORS = ['#8B0000', '#A52A2A', '#B22222', '#7C0A02', '#5C0000'];
    const FLOWER_COLORS = ['#FF8C00', '#FFA500', '#FF4500', '#FFD700', '#FF69B4', '#FFFFFF', '#FFB6C1']; // Marigold, Rose, Jasmine mix

    const createParticle = (x, y, vx, vy, size, type = 'dust') => {
        return {
            x,
            y,
            vx,
            vy,
            size: size || (type === 'flower' ? Math.random() * 4 + 3 : Math.random() * 1.5 + 0.2),
            color: type === 'flower'
                ? FLOWER_COLORS[Math.floor(Math.random() * FLOWER_COLORS.length)]
                : COLORS[Math.floor(Math.random() * COLORS.length)],
            life: 1,
            decay: type === 'flower' ? Math.random() * 0.005 + 0.002 : Math.random() * 0.01 + 0.003,
            angle: Math.random() * Math.PI * 2,
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.1,
            type
        };
    };

    const burst = (side, width, height) => {
        const x = side === 'left' ? -50 : width + 50;
        const count = 2000;
        for (let i = 0; i < count; i++) {
            const isFlower = Math.random() > 0.92; // 8% chance of being a flower
            const vx = side === 'left' ? Math.random() * 18 + 5 : -(Math.random() * 18 + 5);
            const vy = (Math.random() - 0.5) * 20 - 8;
            particles.current.push(createParticle(x, Math.random() * (height * 0.9), vx, vy, null, isFlower ? 'flower' : 'dust'));
        }
    };

    const spawnHoverParticles = (x, y) => {
        for (let i = 0; i < 15; i++) {
            const isFlower = Math.random() > 0.95; // Rare flowers on hover
            const vx = (Math.random() - 0.5) * 6;
            const vy = (Math.random() - 0.5) * 3 - 1;
            particles.current.push(createParticle(x, y, vx, vy, null, isFlower ? 'flower' : 'dust'));
        }
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const container = containerRef?.current || canvas.parentElement;

        const resize = () => {
            canvas.width = container.offsetWidth;
            canvas.height = container.offsetHeight;
        };

        window.addEventListener('resize', resize);
        resize();

        if (!isInitialBurstDone.current) {
            setTimeout(() => {
                burst('left', canvas.width, canvas.height);
                burst('right', canvas.width, canvas.height);
                isInitialBurstDone.current = true;
            }, 1000); // 0.9s delay as requested
        }

        const drawPetal = (ctx, x, y, size, color, rotation) => {
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(rotation);
            ctx.fillStyle = color;

            // Beautiful 5-petal flower shape
            for (let i = 0; i < 5; i++) {
                ctx.rotate((Math.PI * 2) / 5);
                ctx.beginPath();
                ctx.moveTo(0, 0);
                // More elegant petal shape
                ctx.bezierCurveTo(size * 1.5, -size * 1.5, size * 1.5, size * 1.5, 0, 0);
                ctx.fill();
            }

            // Flower center
            ctx.fillStyle = '#FFD700'; // Gold center
            ctx.beginPath();
            ctx.arc(0, 0, size * 0.4, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
        };

        let rainCounter = 0;
        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Continuous Slow Flower Rain after initial burst
            if (isInitialBurstDone.current) {
                rainCounter++;
                // Spawn a flower roughly every 30-40 frames to keep 6-7 on screen
                if (rainCounter % 40 === 0) {
                    const rx = Math.random() * canvas.width;
                    const ry = -20;
                    const rvx = (Math.random() - 0.5) * 1;
                    const rvy = Math.random() * 1.5 + 0.5; // Slow downward speed
                    const rSize = Math.random() * 3 + 4; // Different sizes
                    particles.current.push(createParticle(rx, ry, rvx, rvy, rSize, 'flower'));
                }
            }

            for (let i = particles.current.length - 1; i >= 0; i--) {
                const p = particles.current[i];
                // Apply different physics for "Rain" vs "Burst" flowers
                // Rain flowers don't decay as fast and aren't affected by heavy burst friction
                if (p.y < 0 && p.type === 'flower') {
                    p.vy = Math.random() * 1 + 0.5;
                }

                p.vy += p.type === 'flower' ? GRAVITY * 0.4 : GRAVITY;
                p.vx *= FRICTION;
                p.x += p.vx;
                p.y += p.vy;

                // Slower decay for continuous rain flowers
                p.life -= p.type === 'flower' ? 0.002 : p.decay;
                p.rotation += p.rotationSpeed;

                if (p.life <= 0 || p.y > canvas.height + 20) {
                    particles.current.splice(i, 1);
                    continue;
                }

                ctx.globalAlpha = Math.max(0, p.life);

                if (p.type === 'flower') {
                    drawPetal(ctx, p.x, p.y, p.size, p.color, p.rotation);
                } else {
                    ctx.fillStyle = p.color;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            animationFrameId.current = requestAnimationFrame(animate);
        };

        animate();

        const handleMouseMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            spawnHoverParticles(x, y);
        };

        container.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationFrameId.current);
            container.removeEventListener('mousemove', handleMouseMove);
        };
    }, [containerRef]);

    return (
        <canvas
            ref={canvasRef}
            className="holi-canvas"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 5,
            }}
        />
    );
};

export default HoliColorEffect;
